import { useEffect } from 'react';

/**
 * Клавиатурные сокращения кабинета (спецификация, разд. 15).
 *
 * Диспетчер за смену открывает сотни карточек, и мышь здесь — не выбор, а потеря
 * времени. Сокращения намеренно повторяют привычные: `/` и `⌘K` — поиск, `J`/`K` —
 * движение по списку, `Enter` — открыть, `Esc` — закрыть.
 *
 * Во время ввода текста сокращения молчат: буква `j`, набранная в поиске, обязана
 * попасть в поле, а не увести фокус на другую строку. Это самая частая ошибка таких
 * слоёв, и проверяется она тестом, а не вниманием.
 */

export interface Shortcuts {
  readonly onSearch?: () => void;
  readonly onNext?: () => void;
  readonly onPrevious?: () => void;
  readonly onOpen?: () => void;
  readonly onClose?: () => void;
  readonly onAssign?: () => void;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName.toLowerCase();

  // `isContentEditable` в некоторых средах отсутствует вовсе, поэтому сравнение
  // строгое: результат обязан быть булевым, а не «похожим на ложь».
  return (
    tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable === true
  );
}

/** Разбирает событие в действие. Отдельно от эффекта — чтобы проверять тестом. */
export function shortcutFor(event: {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly target: EventTarget | null;
}): keyof Shortcuts | null {
  const typing = isTypingTarget(event.target);

  // Поиск открывается даже из поля ввода: ⌘K — это «перейти к поиску», а не буква.
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    return 'onSearch';
  }

  // Esc работает и при вводе: это выход, а не команда.
  if (event.key === 'Escape') {
    return 'onClose';
  }

  if (typing) {
    return null;
  }

  switch (event.key) {
    case '/':
      return 'onSearch';
    case 'j':
    case 'J':
      return 'onNext';
    case 'k':
    case 'K':
      return 'onPrevious';
    case 'Enter':
      return 'onOpen';
    case 'a':
    case 'A':
      return 'onAssign';
    default:
      return null;
  }
}

export function useShortcuts(handlers: Shortcuts): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const action = shortcutFor(event);

      if (action === null) {
        return;
      }

      const handler = handlers[action];

      if (handler === undefined) {
        return;
      }

      event.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlers]);
}
